import { c as _c } from "react/compiler-runtime";
import { z } from "zod";
import { useTranslations } from "next-intl";
import { ValidatedInput, ValidatedSelect } from "@/components/form/validated-field";

// ============================================================================
// Schema & Types
// ============================================================================

const field = {
  firstName: {
    key: "firstName" as const,
    schema: z.string().min(1, "First name is required"),
    defaultValue: ""
  },
  lastName: {
    key: "lastName" as const,
    schema: z.string().min(1, "Last name is required"),
    defaultValue: ""
  },
  email: {
    key: "email" as const,
    schema: z.string().email("Please enter a valid email address"),
    defaultValue: ""
  },
  contactNumber: {
    key: "contactNumber" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  businessName: {
    key: "businessName" as const,
    schema: z.string().min(1, "Business name is required"),
    defaultValue: ""
  },
  website: {
    key: "website" as const,
    schema: z.string().url().optional().or(z.literal("")),
    defaultValue: ""
  },
  street: {
    key: "street" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  city: {
    key: "city" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  zipCode: {
    key: "zipCode" as const,
    schema: z.string().optional(),
    defaultValue: ""
  },
  country: {
    key: "country" as const,
    schema: z.string().optional(),
    defaultValue: ""
  }
};
export const signupFormSchema = z.object({
  firstName: field.firstName.schema,
  lastName: field.lastName.schema,
  email: field.email.schema,
  contactNumber: field.contactNumber.schema,
  businessName: field.businessName.schema,
  website: field.website.schema,
  street: field.street.schema,
  city: field.city.schema,
  zipCode: field.zipCode.schema,
  country: field.country.schema
});
export type SignupFormData = z.infer<typeof signupFormSchema>;

// ============================================================================
// Field Components
// ============================================================================

type FieldProps = Readonly<{
  disabled?: boolean;
}>;
function FirstName(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("firstNameLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("firstNamePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.firstName.key} id={field.firstName.key} className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function LastName(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("lastNameLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("lastNamePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.lastName.key} id={field.lastName.key} className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Email(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("emailLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("emailPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.email.key} id={field.email.key} type="email" className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ContactNumber(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("contactNumberLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("contactNumberPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.contactNumber.key} id={field.contactNumber.key} type="tel" className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function BusinessName(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("businessNameLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("businessNamePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.businessName.key} id={field.businessName.key} className="w-full" placeholder={t2} required={true} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Website(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("websiteLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("websitePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.website.key} id={field.website.key} type="url" className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Street(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("streetLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("streetPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.street.key} id={field.street.key} className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function City(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("cityLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("cityPlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.city.key} id={field.city.key} className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ZipCode(t0) {
  const $ = _c(8);
  const {
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== t) {
    t1 = t("zipCodeLabel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("zipCodePlaceholder");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== disabled || $[5] !== t1 || $[6] !== t2) {
    t3 = <ValidatedInput label={t1} name={field.zipCode.key} id={field.zipCode.key} className="w-full" placeholder={t2} disabled={disabled} />;
    $[4] = disabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Country(t0) {
  const $ = _c(11);
  const {
    countries,
    disabled
  } = t0;
  const t = useTranslations("home.signUp.form");
  let t1;
  if ($[0] !== countries) {
    t1 = countries.map(_temp);
    $[0] = countries;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const countryOptions = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("countryLabel");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("countryPlaceholder");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== countryOptions || $[7] !== disabled || $[8] !== t2 || $[9] !== t3) {
    t4 = <ValidatedSelect label={t2} name={field.country.key} className="w-full" placeholder={t3} options={countryOptions} disabled={disabled} />;
    $[6] = countryOptions;
    $[7] = disabled;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp(country) {
  return {
    value: country.id.toString(),
    label: country.name
  };
}
export const FormField = {
  FirstName,
  LastName,
  Email,
  ContactNumber,
  BusinessName,
  Website,
  Street,
  City,
  ZipCode,
  Country
};